import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '../ui/Button';
import styles from './CookieConsent.module.css';

const COOKIE_CONSENT_KEY = 'cookieConsent';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setIsVisible(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    saveConsent(allAccepted);
  };

  const handleDecline = () => {
    const declined = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    saveConsent(declined);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setIsVisible(false);
    console.log('Cookie preferences saved:', prefs);
  };

  if (!isVisible) return null;

  return (
    <div className={styles.cookieBanner}>
      <div className={styles.cookieContent}>
        <div className={styles.cookieHeader}>
          <div className={styles.cookieIcon}>🍪</div>
          <h3 className={styles.cookieTitle}>{t('cookies.title')}</h3>
          <button
            className={styles.closeButton}
            onClick={handleDecline}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <p className={styles.cookieText}>{t('cookies.description')}</p>

        {showDetails && (
          <div className={styles.cookieDetails}>
            <div className={styles.cookieOption}>
              <label className={styles.cookieLabel}>
                <input
                  type="checkbox"
                  checked={preferences.necessary}
                  disabled
                  className={styles.checkbox}
                />
                <div>
                  <strong>{t('cookies.necessary')}</strong>
                  <p className={styles.optionDescription}>
                    {t('cookies.necessaryDescription')}
                  </p>
                </div>
              </label>
            </div>

            <div className={styles.cookieOption}>
              <label className={styles.cookieLabel}>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) =>
                    setPreferences({ ...preferences, analytics: e.target.checked })
                  }
                  className={styles.checkbox}
                />
                <div>
                  <strong>{t('cookies.analytics')}</strong>
                  <p className={styles.optionDescription}>
                    {t('cookies.analyticsDescription')}
                  </p>
                </div>
              </label>
            </div>

            <div className={styles.cookieOption}>
              <label className={styles.cookieLabel}>
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) =>
                    setPreferences({ ...preferences, marketing: e.target.checked })
                  }
                  className={styles.checkbox}
                />
                <div>
                  <strong>{t('cookies.marketing')}</strong>
                  <p className={styles.optionDescription}>
                    {t('cookies.marketingDescription')}
                  </p>
                </div>
              </label>
            </div>
          </div>
        )}

        <div className={styles.cookieButtons}>
          <Button
            variant="secondary"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? t('cookies.hideDetails') : t('cookies.manage')}
          </Button>
          
          {showDetails ? (
            <Button onClick={handleSavePreferences}>
              {t('cookies.savePreferences')}
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={handleDecline}>
                {t('cookies.decline')}
              </Button>
              <Button onClick={handleAcceptAll}>
                {t('cookies.acceptAll')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

